import { Link, useRouterState } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { itemsOf, locate, type NavArea } from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { UserLinks } from './UserLinks.tsx';
import { AdminBadge } from './AdminBadge.tsx';

/**
 * Layout "Foco e comando" (ADR 0128): quase sem moldura. Uma faixa clara com o logo, os atalhos
 * das áreas (se o ajuste pede) e a busca de comandos no meio; o conteúdo fica numa coluna central.
 */
export function FocusBar({
  areas,
  me,
  onSearch,
  links,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
  links: boolean;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  return (
    <div className="hidden shrink-0 items-center gap-3 border-b border-rule bg-paper px-5 py-2 md:flex">
      <Link to="/" aria-label={t('nav.homeBrand')} className="flex shrink-0 items-center">
        <img src={look.symbolUrl} alt="" className="h-7 w-auto max-w-10 object-contain" />
      </Link>
      <AdminBadge />
      {links && (
        <nav
          aria-label={t('nav.label')}
          className="flex min-w-0 items-center gap-1 overflow-x-auto"
        >
          {areas
            .filter((a) => !a.bottom)
            .map((area) => {
              const active = area.id === current?.id;
              return (
                <Link
                  key={area.id}
                  to={itemsOf(area)[0]!.to}
                  aria-current={active ? 'true' : undefined}
                  className={`flex min-h-9 shrink-0 items-center rounded-md px-3 text-sm whitespace-nowrap ${
                    active
                      ? 'bg-tonal font-medium text-ink'
                      : 'text-muted hover:bg-tonal hover:text-ink'
                  }`}
                >
                  {t(area.key)}
                </Link>
              );
            })}
        </nav>
      )}
      <button
        type="button"
        onClick={onSearch}
        className="mx-auto flex min-h-9 w-full max-w-md items-center justify-between gap-2 rounded-md border border-control-border bg-sheet px-3 text-left text-[13px] text-muted"
      >
        <span className="flex items-center gap-2">
          <Search size={14} aria-hidden />
          {t('nav.command')}
        </span>
        <kbd className="rounded border border-rule bg-paper px-1.5 font-mono text-[11px]">
          Ctrl K
        </kbd>
      </button>
      <UserLinks areas={areas} me={me} onSearch={onSearch} search={false} />
    </div>
  );
}
